/**
 * @fileoverview Modal de confirmação para restauração de dados originais
 * @module components/modals/RestoreConfirmationModal
 * 
 * @description
 * Modal de confirmação que aparece antes de restaurar dados originais da DRE.
 * Exibe preview das mudanças que serão perdidas e cria checkpoint automático.
 * 
 * SPRINT 4: Confirmation Modals (Problem #8 - HIGH)
 * Previne perda acidental de edições ao restaurar dados originais.
 * 
 * @version 1.0.0 - Sprint 4
 */

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Box,
  Typography,
  Alert,
  Divider,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Warning as WarningIcon,
  History as HistoryIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Save as SaveIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';

interface Edit {
  company: 'Sétima' | '555 Studios';
  year: number;
  field: string;
  oldValue: number;
  newValue: number;
  percentChange: number;
}

interface RestoreConfirmationModalProps {
  /** Estado de abertura do modal */
  open: boolean;
  /** Callback para fechar modal */
  onClose: () => void;
  /** Callback para confirmar restauração (com checkpoint automático) */
  onConfirm: () => void;
  /** Lista de edições que serão perdidas */
  edits: Edit[];
  /** Nome da versão atual editada */
  versionName?: string;
  /** DIA 9: Loading state durante restauração */
  isRestoring?: boolean;
}

/**
 * Formata valor monetário em milhões
 */
const formatMoney = (value: number): string => {
  const absValue = Math.abs(value);
  const signal = value < 0 ? '-' : '';
  return `${signal}R$ ${absValue.toFixed(2)}M`;
};

/**
 * Formata mudança percentual
 */
const formatPercent = (percent: number): string => {
  // Trata casos de Infinity (quando oldValue = 0)
  if (!isFinite(percent)) {
    return 'N/A (base zero)';
  }
  const absPercent = Math.abs(percent);
  return `${percent >= 0 ? '+' : '-'}${absPercent.toFixed(1)}%`;
};

/**
 * Componente para exibir uma edição individual
 */
const EditRow = ({ edit }: { edit: Edit }) => {
  const isIncrease = edit.newValue > edit.oldValue;
  
  return (
    <Box
      sx={{
        p: 2,
        bgcolor: 'background.paper',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack spacing={1}>
        {/* Cabeçalho da edição */}
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            <Chip
              label={edit.company}
              size="small"
              color={edit.company === 'Sétima' ? 'primary' : 'secondary'}
              sx={{ fontWeight: 600 }}
            />
            <Typography variant="body2" color="text.secondary">
              {edit.year}
            </Typography>
          </Stack>
          
          <Chip
            icon={isIncrease ? <TrendingUpIcon /> : <TrendingDownIcon />}
            label={formatPercent(edit.percentChange)}
            size="small"
            color={isIncrease ? 'success' : 'error'}
            variant="outlined"
          />
        </Stack>

        {/* Campo editado */}
        <Typography variant="body2" fontWeight={600}>
          {edit.field}
        </Typography>

        {/* Valores: Original → Editado */}
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box flex={1}>
            <Typography variant="caption" color="text.secondary">
              Original
            </Typography>
            <Typography variant="body2" color="text.primary">
              {formatMoney(edit.oldValue)}
            </Typography>
          </Box>

          <Typography variant="h6" color="text.secondary">
            →
          </Typography>

          <Box flex={1}>
            <Typography variant="caption" color="text.secondary">
              Editado
            </Typography>
            <Typography
              variant="body2"
              color={isIncrease ? 'success.main' : 'error.main'}
              fontWeight={600}
            >
              {formatMoney(edit.newValue)}
            </Typography>
          </Box>
        </Stack>
      </Stack>
    </Box>
  );
};

/**
 * Modal de confirmação para restauração de dados originais
 * 
 * @example
 * ```tsx
 * <RestoreConfirmationModal
 *   open={confirmModalOpen}
 *   onClose={() => setConfirmModalOpen(false)}
 *   onConfirm={handleConfirmRestore}
 *   edits={editsList}
 *   versionName="Versão v85"
 * />
 * ```
 */
export function RestoreConfirmationModal({
  open,
  onClose,
  onConfirm,
  edits,
  isRestoring = false,
}: RestoreConfirmationModalProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '90vh' },
      }}
    >
      {/* Título com Warning */}
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <WarningIcon color="warning" />
          <Typography variant="h6" component="span">
            Confirmar Restauração dos Dados Originais
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* Alert principal de warning */}
          <Alert severity="warning" icon={<WarningIcon />}>
            <Typography variant="body2" gutterBottom>
              <strong>Atenção:</strong> Esta ação irá descartar TODAS as suas edições e
              restaurar os dados originais do histórico.
            </Typography>
            <Typography variant="body2">
              <strong>{edits.length} edição(ões)</strong> será(ão) perdida(s).
            </Typography>
          </Alert>

          {/* Info sobre checkpoint automático */}
          <Alert severity="info" icon={<HistoryIcon />}>
            <Typography variant="body2">
              <SaveIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
              <strong>Checkpoint Automático:</strong> Antes de restaurar, criaremos um
              snapshot automático das suas edições. Você poderá recuperá-las depois no
              Histórico de Versões.
            </Typography>
          </Alert>

          <Divider />

          {/* Título da lista de edições */}
          <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AssessmentIcon sx={{ fontSize: 20 }} />
              Edições que serão perdidas:
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Veja abaixo todas as mudanças que você fez e que serão revertidas
            </Typography>
          </Box>

          {/* Lista de edições */}
          <Stack spacing={2}>
            {edits.length > 0 ? (
              edits.map((edit, index) => (
                <EditRow key={`${edit.company}-${edit.year}-${edit.field}-${index}`} edit={edit} />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Nenhuma edição detectada
              </Typography>
            )}
          </Stack>

          {/* Resumo estatístico */}
          {edits.length > 0 && (
            <Box
              sx={{
                p: 2,
                bgcolor: 'background.default',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Stack direction="row" spacing={3} justifyContent="center">
                <Box textAlign="center">
                  <Typography variant="h4" color="primary.main">
                    {edits.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Edições Totais
                  </Typography>
                </Box>

                <Divider orientation="vertical" flexItem />

                <Box textAlign="center">
                  <Typography variant="h4" color="primary.main">
                    {new Set(edits.map((e) => e.company)).size}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Empresas Afetadas
                  </Typography>
                </Box>

                <Divider orientation="vertical" flexItem />

                <Box textAlign="center">
                  <Typography variant="h4" color="primary.main">
                    {new Set(edits.map((e) => e.year)).size}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Anos Modificados
                  </Typography>
                </Box>
              </Stack>
            </Box>
          )}
        </Stack>
      </DialogContent>

      {/* Botões de ação */}
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined" size="large" disabled={isRestoring}>
          Cancelar
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="error"
          size="large"
          disabled={isRestoring}
          startIcon={isRestoring ? <CircularProgress size={16} color="inherit" /> : <WarningIcon />}
        >
          {isRestoring ? 'Restaurando...' : 'Confirmar Restauração'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
